import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import Layout from '../../components/layout/Layout';
import salesmanService from '../../services/salesmanService';
import locationService from '../../services/locationService';
import { getCurrentUser } from '../../services/authService';

const SalesmenPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [salesmen, setSalesmen] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [locationFilter, setLocationFilter] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    locationId: '',
    companyId: '',
  });
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  const fetchSalesmen = async () => {
    try {
      setLoading(true);
      const data = await salesmanService.getAll({
        page,
        size: rowsPerPage,
        locationId: locationFilter || undefined,
      });
      setSalesmen(data.content || []);
      setTotalElements(data.totalElements != null ? data.totalElements : 0);
    } catch (error) {
      console.error('Error fetching salesmen:', error);
      enqueueSnackbar('Failed to load salesmen', { variant: 'error' });
      setSalesmen([]);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const res = await locationService.list({ page: 0, size: 100, sort: 'name', direction: 'ASC' });
      setLocations(res.content || []);
    } catch (err) {
      console.error('Error fetching locations:', err);
      setLocations([]);
    }
  };

  useEffect(() => {
    fetchSalesmen();
  }, [page, rowsPerPage, locationFilter]);

  useEffect(() => {
    fetchLocations();
  }, []);

  const handleOpenDialog = (salesman = null) => {
    const companyId = getCurrentUser()?.companyId ? parseInt(getCurrentUser().companyId, 10) : null;
    if (salesman) {
      setFormData({
        name: salesman.name || '',
        phone: salesman.phone || '',
        email: salesman.email || '',
        address: salesman.address || '',
        locationId: salesman.locationId ?? '',
        companyId: salesman.companyId || companyId,
      });
      setEditMode(true);
      setEditId(salesman.id);
    } else {
      setFormData({
        name: '',
        phone: '',
        email: '',
        address: '',
        locationId: locationFilter || '',
        companyId: companyId || '',
      });
      setEditMode(false);
      setEditId(null);
    }
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => setOpenDialog(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.phone.trim()) errors.phone = 'Phone is required';
    if (!formData.locationId) errors.locationId = 'Location is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    const payload = {
      ...formData,
      companyId: formData.companyId || getCurrentUser()?.companyId,
      locationId: parseInt(formData.locationId, 10),
    };
    try {
      setLoading(true);
      if (editMode) {
        await salesmanService.update(editId, payload);
        enqueueSnackbar('Salesman updated successfully', { variant: 'success' });
      } else {
        await salesmanService.create(payload);
        enqueueSnackbar('Salesman created successfully', { variant: 'success' });
      }
      handleCloseDialog();
      fetchSalesmen();
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to save salesman';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this salesman?')) return;
    try {
      setLoading(true);
      await salesmanService.delete(id);
      enqueueSnackbar('Salesman deleted successfully', { variant: 'success' });
      fetchSalesmen();
    } catch (error) {
      enqueueSnackbar(error.response?.data?.message || 'Failed to delete salesman', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (e, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h5">Salesmen</Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Location</InputLabel>
              <Select
                value={locationFilter}
                label="Location"
                onChange={(e) => { setLocationFilter(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All locations</MenuItem>
                {locations.map((loc) => (
                  <MenuItem key={loc.id} value={loc.id}>{loc.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button startIcon={<RefreshIcon />} onClick={fetchSalesmen} disabled={loading} variant="outlined">
              Refresh
            </Button>
            <Button startIcon={<AddIcon />} onClick={() => handleOpenDialog(null)} variant="contained">
              Add Salesman
            </Button>
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Create salesmen for each location. Select a salesman at POS when placing a bill to attribute sales for reports.
        </Typography>

        <Paper sx={{ width: '100%' }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && salesmen.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center"><CircularProgress size={28} /></TableCell>
                  </TableRow>
                ) : salesmen.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">No salesmen found. Add one for your location.</TableCell>
                  </TableRow>
                ) : (
                  salesmen.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.phone}</TableCell>
                      <TableCell>{row.email || '–'}</TableCell>
                      <TableCell>{row.locationName || '–'}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" color="primary" onClick={() => handleOpenDialog(row)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalElements}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>

        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>{editMode ? 'Edit Salesman' : 'Add Salesman'}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <TextField
                  name="name"
                  label="Name *"
                  value={formData.name}
                  onChange={handleInputChange}
                  fullWidth
                  error={Boolean(formErrors.name)}
                  helperText={formErrors.name}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="phone"
                  label="Phone *"
                  value={formData.phone}
                  onChange={handleInputChange}
                  fullWidth
                  error={Boolean(formErrors.phone)}
                  helperText={formErrors.phone}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="email"
                  label="Email (optional)"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="address"
                  label="Address (optional)"
                  value={formData.address}
                  onChange={handleInputChange}
                  fullWidth
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth error={Boolean(formErrors.locationId)}>
                  <InputLabel>Location *</InputLabel>
                  <Select
                    name="locationId"
                    value={formData.locationId ?? ''}
                    onChange={handleInputChange}
                    label="Location *"
                  >
                    <MenuItem value="">Select location</MenuItem>
                    {locations.map((loc) => (
                      <MenuItem key={loc.id} value={loc.id}>{loc.name}</MenuItem>
                    ))}
                  </Select>
                  {formErrors.locationId && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>{formErrors.locationId}</Typography>
                  )}
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained" disabled={loading}>
              {editMode ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default SalesmenPage;
